/**
 * Typed widget-side surface for the Assistant API (P1-058 + P3-022a).
 *
 * Wraps four endpoints documented in
 * [planning/API-CONTRACT.md](../../../planning/API-CONTRACT.md) §5:
 *
 *   1. `POST {sessionEndpoint}`               — mint a conversation
 *   2. `POST {messageEndpoint}` — send a user message and receive
 *      the full assistant reply as JSON (non-streaming)
 *   3. `POST {messageStreamEndpoint}` — same request body; reply as
 *      SSE (`text/event-stream`)
 *   4. `POST {endEndpoint}` with `{ id }` — end the conversation
 *   5. `POST {historyEndpoint}`             — fetch persisted chat
 *      history (`{ id }` body)
 *   6. `POST {feedbackEndpoint}`            — submit per-turn or
 *      end-of-chat feedback
 *
 * The widget calls these endpoints through this module rather than
 * inlining `fetch` wherever needed. Centralising the wire-format
 * concern here means:
 *
 *  - **One auth seam.** The constructor accepts a fetch-shaped
 *    function; in production this is the element's
 *    `authedFetch.bind(element)`, which routes through the P1-057
 *    auth client (Bearer + 401-retry + Sitecore-cookie pass-through).
 *    Tests substitute a `vi.fn()` and assert URL/method/body shape
 *    without standing up a real auth flow.
 *  - **One error surface.** `sendMessageStream` yields a single
 *    `AssistantStreamEvent` discriminated union: `'delta'`,
 *    `'complete'`, and `'error'`. Pre-stream HTTP failures
 *    (validation, `session_not_found`, 401, 5xx) are translated
 *    into synthetic `'error'` events so the widget's stream loop
 *    has exactly one terminal shape to handle.
 *  - **No widget knowledge in the SSE parser.** The transport-level
 *    event-stream framing lives in `./sse-parser.ts`; this module
 *    is the only place that maps SSE event names + JSON payloads
 *    onto the widget's typed events.
 *
 * Caller-initiated aborts (via `AbortSignal`) terminate
 * `sendMessageStream` iteration cleanly without yielding an error
 * event — the caller already knows they cancelled. This is what
 * lets the widget abort an in-flight stream on confirmed-close
 * without surfacing a spurious "connection lost" bubble.
 *
 * The DTO shapes below are 1:1 serialisations of the C# records in
 * `src/MWI.Ai.EcommAssistant.Core/Models/`:
 *  - `AssistantSession.cs`        ↔ {@link AssistantSession}
 *  - `AssistantRequest.cs`        ↔ {@link AssistantRequest}
 *  - `AssistantResponse.cs` +
 *    `AssistantAction`            ↔ {@link AssistantResponse} +
 *                                   {@link AssistantAction}
 *
 * Any change to those records is a contract change and requires the
 * version bump documented in API-CONTRACT.md §8.
 */

import { parseSseStream, type SseEvent } from './sse-parser.js';

function debugAssistantApiRequest(method: string, url: string): void {
  console.log(`[mwi-assistant:api] starting request: ${method} ${url}`);
}

/**
 * Default relative endpoint for {@link CreateAssistantApiClientOptions.sessionEndpoint}.
 * Appended to {@link CreateAssistantApiClientOptions.apiBase} unless the
 * configured value is absolute.
 */
export const DEFAULT_SESSION_ENDPOINT = '/api/assistant/session';

/**
 * Default relative endpoint for {@link CreateAssistantApiClientOptions.messageEndpoint}.
 */
export const DEFAULT_MESSAGE_ENDPOINT = '/api/assistant/message';

/**
 * Default relative endpoint for
 * {@link CreateAssistantApiClientOptions.messageStreamEndpoint}.
 */
export const DEFAULT_MESSAGE_STREAM_ENDPOINT = '/api/assistant/message/stream';

/**
 * Default relative endpoint for {@link CreateAssistantApiClientOptions.historyEndpoint}.
 * Appended to {@link CreateAssistantApiClientOptions.apiBase} unless the
 * configured value is absolute.
 */
export const DEFAULT_HISTORY_ENDPOINT = '/api/assistant/history';

/**
 * Default relative endpoint for {@link CreateAssistantApiClientOptions.endEndpoint}.
 * Appended to {@link CreateAssistantApiClientOptions.apiBase} unless the
 * configured value is absolute.
 */
export const DEFAULT_END_ENDPOINT = '/api/assistant/session/end';

/**
 * Default relative endpoint for {@link CreateAssistantApiClientOptions.feedbackEndpoint}.
 */
export const DEFAULT_FEEDBACK_ENDPOINT = '/api/feedback';

/* ============================================================== *
 *  DTO shapes                                                    *
 * ============================================================== */

/**
 * A UI action suggested by the assistant.
 *
 * Discriminated on `type`; mirrors the C# `AssistantAction`
 * polymorphic union in
 * `src/MWI.Ai.EcommAssistant.Core/Models/AssistantResponse.cs`
 * (P1-058a). Wire shape is documented in
 * [planning/API-CONTRACT.md](../../../planning/API-CONTRACT.md)
 * §6.1.
 *
 * Variants:
 *
 *  - {@link IntentAction} (`'intent'`) — welcome-bubble starter
 *    chip. Click submits the chip's `label` as the next user
 *    message; `parameters.intentId` is forwarded as
 *    `clientContext.selectedIntentId` on the next
 *    `/message/stream` request.
 *  - {@link LinkOutAction} (`'link-out'`) — outlined chip beneath
 *    the assistant bubble that opens `url` in a new tab.
 *    **Rendered today (P1-058a).**
 *  - {@link FollowUpPromptAction} (`'follow-up-prompt'`) —
 *    outlined chip beneath the assistant bubble that submits its
 *    `label` as the next user message. **Contract-only as of
 *    P1-058a — render path lands in P1-058c** once the agent team
 *    commits to emitting this type.
 *
 * Forward-compat: any payload whose `type` value isn't one of the
 * three variants above is silently dropped by
 * {@link normalizeActions} during SSE / session parsing, and a
 * `console.warn` is emitted in dev builds. The `MwiAssistantTurn`
 * carrying `actions` therefore only ever sees known shapes — the
 * widget's render path is exhaustively typed.
 */
export type AssistantAction =
  | IntentAction
  | LinkOutAction
  | FollowUpPromptAction;

/**
 * Welcome-bubble starter chip. Future task P1-067 (server-driven
 * prompts) will switch the hard-coded local rail to populate from
 * `AssistantSession.initialActions`; the wire shape is already
 * present today, the widget just doesn't read from it yet.
 */
export interface IntentAction {
  readonly type: 'intent';
  readonly label: string;
  readonly parameters?: Readonly<Record<string, string>>;
}

/**
 * Outlined chip below the assistant bubble. Click opens
 * {@link url} in a new tab via
 * `<a href={url} target="_blank" rel="noopener noreferrer">`.
 */
export interface LinkOutAction {
  readonly type: 'link-out';
  readonly label: string;
  readonly url: string;
}

/**
 * Outlined chip below the assistant bubble. Click submits
 * {@link AssistantAction.label} as the user's next message,
 * identical to the {@link IntentAction} pathway. Render path is
 * deferred to P1-058c per
 * [planning/figma/TOKENS.md](../../../planning/figma/TOKENS.md)
 * Open question #13.
 */
export interface FollowUpPromptAction {
  readonly type: 'follow-up-prompt';
  readonly label: string;
  readonly parameters?: Readonly<Record<string, string>>;
}

/**
 * Set of `type` values that {@link normalizeActions} accepts. Any
 * payload whose `type` is outside this set is dropped silently
 * (with a dev-mode `console.warn`) so a future server-emitted
 * action kind can't crash the widget — the universal forward-
 * compat policy that P1-058's SSE parser already applies to
 * unknown event names.
 */
const KNOWN_ACTION_TYPES: ReadonlySet<AssistantAction['type']> = new Set([
  'intent',
  'link-out',
  'follow-up-prompt',
]);

/**
 * Server response from `POST /api/assistant/session`. The widget
 * stores `conversationId` for the lifetime of the conversation and
 * carries it on every subsequent `/message/stream` request and on
 * the visibility-event detail payload.
 *
 * `welcomeMessage` and `initialActions` are observable today but
 * unused — the widget renders a hard-coded welcome bubble + chip
 * rail per the Figma `Chatbot_default start` shell. Future task
 * P1-067 (server-driven prompts) will swap the hard-coded data for
 * these fields without a contract change.
 *
 * Some hosts return only `{ "conversationId": "…" }` from a BFF;
 * {@link parseAssistantSessionBody} fills `createdAt`,
 * `welcomeMessage`, and `initialActions` so the widget can still
 * stream messages.
 */
export interface AssistantSession {
  readonly conversationId: string;
  readonly createdAt: string;
  readonly welcomeMessage: string;
  readonly initialActions: readonly AssistantAction[];
}

/**
 * Request body sent on `POST /api/assistant/message/stream`. The
 * server requires `conversationId` to be a non-empty GUID; passing
 * `Guid.Empty` or an empty string returns a `400` (handled here as
 * a synthetic `'error'` stream event with `errorCode:
 * 'invalid_request'`).
 *
 * `clientContext` is optional free-form hints from the UI. Today
 * the widget never sets it; once intent-button payloads land
 * (P1-022 + P3 follow-ups) the active intent id will flow through
 * here as `{ selectedIntentId: '…' }`.
 */
export interface AssistantRequest {
  readonly conversationId: string;
  readonly message: string;
  readonly clientContext?: Readonly<Record<string, string>>;
}

/**
 * Final assembled assistant response, carried in the SSE
 * `complete` frame's `finalResponse` payload. Mirrors
 * `AssistantResponse.cs`.
 */
export interface AssistantResponse {
  readonly conversationId: string;
  readonly message: string;
  readonly actions: readonly AssistantAction[];
}

/**
 * One event in the streaming-message iterator. Discriminated by
 * `type`; the widget's stream loop pattern-matches on this to
 * grow / finalise / error-out the trailing assistant turn.
 *
 *  - `delta`    — incremental text. Multiple frames per turn.
 *                 The first delta of a turn is the moment the
 *                 thinking indicator should be replaced by a
 *                 growing assistant bubble.
 *  - `complete` — terminal success. Carries the fully assembled
 *                 reply (so a hypothetical caller that only cares
 *                 about the final text can ignore deltas) plus the
 *                 `actions[]` payload.
 *  - `error`    — terminal failure. Either a real SSE `error`
 *                 frame from the server OR a synthetic event the
 *                 client manufactured for a pre-stream HTTP
 *                 failure / transport drop. Same downstream
 *                 handling either way: swap the assistant turn to
 *                 an error bubble with a Try-again affordance.
 */
export type AssistantStreamEvent =
  | { readonly type: 'delta'; readonly textDelta: string }
  | { readonly type: 'complete'; readonly finalResponse: AssistantResponse }
  | {
      readonly type: 'error';
      readonly errorCode: string;
      readonly errorMessage: string;
    };

/* ============================================================== *
 *  Feedback (P3-022a) — wire shape only                           *
 *                                                                 *
 *  Mirrors the C# polymorphic union in                            *
 *  `src/MWI.Ai.EcommAssistant.Core/Models/FeedbackRequest.cs`     *
 *  byte-for-byte. Documented in                                   *
 *  [planning/API-CONTRACT.md](../../../planning/API-CONTRACT.md)  *
 *  §5.5.                                                          *
 *                                                                 *
 *  IMPORTANT: the server endpoint does NOT exist yet — endpoint   *
 *  registration lands with P3-022b. Calls made today return 404.  *
 *  Two parallel UI agents (P3-023a thumbs primitive, P3-025a      *
 *  negative-reason inline card) will start wiring against this    *
 *  surface immediately so the per-turn flow is end-to-end ready   *
 *  the moment the server side lands.                              *
 * ============================================================== */

/**
 * Reason chip the user picked on a negative rating. Wire values
 * are lowercase singular adjectives — the negative-reason card
 * (P3-025a) renders one checkbox per value.
 *
 * **Adding a new reason is a contract change** that requires the
 * matching C# enum (`FeedbackReason`) to grow in lockstep
 * (planning/API-CONTRACT.md §8).
 */
export type FeedbackReason =
  | 'inaccurate'
  | 'irrelevant'
  | 'repetitive'
  | 'unhelpful';

/**
 * How the negative-reason follow-up card resolved on a negative
 * rating. Captures the three terminal states of the per-turn
 * (P3-023b) and end-of-chat (P3-024a) negative flow:
 *
 *  - `'reasons'`   — user picked one or more reason chips and
 *                    clicked Submit. `reasons[]` is populated.
 *  - `'dismissed'` — user explicitly dismissed the card (Dismiss
 *                    button or Esc), declining to elaborate. The
 *                    thumb-down state is preserved on the row;
 *                    the user is on record as "not helpful".
 *                    `reasons[]` is absent.
 *  - `'abandoned'` — user confirmed-closed the chat session while
 *                    the card was still mounted. The widget
 *                    auto-flushes the pending negative on the way
 *                    through `_endSession` so the signal is not
 *                    lost. `reasons[]` is absent.
 *
 * Mirrors the C# `FeedbackFollowUp` enum in
 * `src/MWI.Ai.EcommAssistant.Core/Models/FeedbackRequest.cs`. Wire
 * values are lowercase. Adding a new variant is a contract change
 * (planning/API-CONTRACT.md §8) — both ends move in lockstep.
 */
export type FeedbackFollowUp = 'reasons' | 'dismissed' | 'abandoned';

/**
 * Discriminated union of all feedback shapes the widget may POST
 * to `/api/feedback`. Two variants exist today:
 *
 *  - `'per-turn'` — thumbs up/down attached to a specific
 *    assistant `turnId`. Drives the per-turn thumbs primitive
 *    (P3-023a) plus the negative-reason follow-up card (P3-025a).
 *  - `'end-of-chat'` — overall-experience prompt that fires on
 *    session close. **No `turnId`** — end-of-chat feedback is
 *    session-scoped, not turn-scoped.
 *
 * **Negative is a two-step flow** (per-turn variant): thumbs-down
 * opens the reason card; the API call is deferred until the
 * follow-up resolves. `followUp` captures the resolution and is
 * therefore required-on-negative; `reasons[]` is required-and-
 * non-empty only when `followUp === 'reasons'`. Positive feedback
 * has no follow-up card today and omits both fields. The
 * required/forbidden guards are enforced server-side (P3-022b),
 * not by the type — see API-CONTRACT.md §5.5 for the full rule
 * matrix.
 */
export type FeedbackPayload =
  | {
      readonly type: 'per-turn';
      readonly conversationId: string;
      readonly turnId: string;
      readonly sentiment: 'positive' | 'negative';
      readonly followUp?: FeedbackFollowUp;
      readonly reasons?: readonly FeedbackReason[];
    }
  | {
      readonly type: 'end-of-chat';
      readonly conversationId: string;
      readonly sentiment: 'positive' | 'negative';
      readonly followUp?: FeedbackFollowUp;
      readonly reasons?: readonly FeedbackReason[];
    };

/**
 * Server response from `POST /api/feedback`. Acknowledges that the
 * feedback record was either persisted or queued; the host doesn't
 * distinguish between the two because retry semantics are owned by
 * the storage layer (P3-020 / P3-021).
 */
export interface FeedbackResponse {
  readonly accepted: boolean;
  readonly feedbackId: string;
}

/**
 * Stable error kinds emitted by {@link AssistantApiClient.submitFeedback}.
 * Matches the documented error responses in API-CONTRACT.md §5.5
 * and uses the same status-driven mapping the streaming client
 * applies to pre-stream HTTP failures (P1-058):
 *
 *  - `'invalid-request'` — HTTP 400 (validation)
 *  - `'unauthorized'`    — HTTP 401 (no/expired auth)
 *  - `'rate-limited'`    — HTTP 429 (`assistant-feedback` policy)
 *  - `'server-error'`    — HTTP 5xx (covers the 404 `endpoint not
 *                          implemented yet` case as well; see
 *                          submitFeedback for why)
 */
export type FeedbackErrorKind =
  | 'invalid-request'
  | 'unauthorized'
  | 'rate-limited'
  | 'server-error';

/* ============================================================== *
 *  Public client surface                                          *
 * ============================================================== */

/**
 * Fetch-shaped function the api-client uses for every request.
 *
 * In production the widget passes `(input, init) => this.authedFetch(input, init)`
 * so each call routes through the P1-057 auth helper. In tests
 * this is a `vi.fn()` returning canned `Response` instances.
 *
 * Typed identically to the global `fetch` so the widget's
 * authedFetch (which already matches that signature) drops in
 * without an adapter, and so a test can also pass `fetch` directly
 * for an integration probe.
 */
export type AuthedFetch = (
  input: RequestInfo | URL,
  init?: RequestInit
) => Promise<Response>;

export interface AssistantApiClient {
  /**
   * Mint a fresh conversation. With default `auto-open`, the widget
   * calls this on disclaimer acceptance (and on connect when
   * sessionStorage shows a prior acceptance). With host-controlled
   * open (`auto-open="false"`), the widget defers until {@link open}
   * so mount alone makes no data calls.
   */
  startSession(signal?: AbortSignal): Promise<AssistantSession>;

  /**
   * Send a message and stream the reply. Yields
   * {@link AssistantStreamEvent}s in arrival order; the iterator
   * terminates after a `'complete'` or `'error'` event lands (or
   * sooner, if the caller aborts via `signal`).
   *
   * Caller-initiated aborts terminate iteration without yielding
   * an event — the caller already knows they cancelled and any
   * synthetic error would just be noise. Server-initiated and
   * transport-level failures DO yield a synthetic `'error'`
   * event so the widget can surface the error UI.
   */
  sendMessageStream(
    request: AssistantRequest,
    signal?: AbortSignal
  ): AsyncIterable<AssistantStreamEvent>;

  /**
   * Send a message and await the full JSON response from
   * `POST /api/assistant/message` (non-streaming). Same request body
   * as {@link sendMessageStream}; mirrors the same HTTP error
   * taxonomy via {@link AssistantApiError} on non-2xx, transport
   * failure, or malformed success body.
   */
  sendMessage(
    request: AssistantRequest,
    signal?: AbortSignal
  ): Promise<AssistantResponse>;

  /**
   * End a conversation server-side. Called from the widget's
   * confirmed-close path so the `AssistantConversation` is reaped
   * promptly rather than waiting for the 30-minute idle eviction.
   *
   * Idempotent on the server (per API-CONTRACT.md §5.4) — calling
   * with an unknown / already-deleted `conversationId` still
   * resolves successfully.
   */
  endSession(conversationId: string, signal?: AbortSignal): Promise<void>;

  /**
   * Fetch chat history for an active conversation via
   * `POST {historyEndpoint}` with `{ id }` in the body.
   * Returns the parsed JSON body on success. Wire shape is host/BFF-
   * specific; the widget accepts `messages[]` (`role`, `content`,
   * `timestamp`) and a legacy `turns[]` (`role`, `message`) alias.
   */
  getChatHistory(
    conversationId: string,
    signal?: AbortSignal
  ): Promise<unknown>;

  /**
   * Submit user feedback (per-turn thumbs or end-of-chat overall
   * experience) to `POST /api/feedback`. Documented in
   * [planning/API-CONTRACT.md](../../../planning/API-CONTRACT.md)
   * §5.5 (P3-022a).
   *
   * **Wire shape only.** The server endpoint is not yet
   * registered — endpoint handler lands with P3-022b — so calls
   * made against the dev API today return `404`, which surfaces
   * here as a `FeedbackError { kind: 'server-error' }`. UI agents
   * (P3-023a per-turn thumbs, P3-025a negative-reason card) can
   * still wire against this method today and will light up
   * end-to-end the moment P3-022b lands without a widget change.
   *
   * Resolves with the parsed {@link FeedbackResponse} on a 2xx;
   * throws a {@link FeedbackError} on any non-2xx, network
   * failure, or malformed body. The aborted-by-caller case
   * propagates the underlying `AbortSignal.reason` (matching the
   * platform's native `fetch` abort behaviour) — we don't wrap it
   * because the caller already knows they aborted and a typed
   * FeedbackError would just be noise.
   */
  submitFeedback(
    payload: FeedbackPayload,
    signal?: AbortSignal
  ): Promise<FeedbackResponse>;
}

export interface CreateAssistantApiClientOptions {
  /**
   * Base URL of the Assistant API (e.g. `https://assistant.example`).
   * Path components are appended verbatim; the value should NOT
   * include a trailing slash. An empty string is allowed in unit
   * tests where the mock fetch ignores the URL.
   */
  readonly apiBase: string;

  /**
   * Fetch-shaped function. In production this is the widget's
   * `authedFetch` so every request carries the Bearer JWT (P1-057);
   * in tests, a `vi.fn()`.
   */
  readonly fetch: AuthedFetch;

  /**
   * Endpoint for {@link AssistantApiClient.startSession}. Relative values
   * are appended to {@link apiBase}; absolute `http(s)://` URLs are
   * used as-is.
   */
  readonly sessionEndpoint?: string;

  /**
   * Endpoint for {@link AssistantApiClient.sendMessage}. Relative values
   * are appended to {@link apiBase}; absolute `http(s)://` URLs are
   * used as-is.
   */
  readonly messageEndpoint?: string;

  /**
   * Endpoint for {@link AssistantApiClient.sendMessageStream}. Relative
   * values are appended to {@link apiBase}; absolute `http(s)://` URLs
   * are used as-is.
   */
  readonly messageStreamEndpoint?: string;

  /**
   * Endpoint for {@link AssistantApiClient.getChatHistory}. Relative values
   * are appended to {@link apiBase}; absolute `http(s)://` URLs are
   * used as-is. An empty string disables chat-history fetches.
   */
  readonly historyEndpoint?: string;

  /**
   * Endpoint for {@link AssistantApiClient.endSession}. Relative values
   * are appended to {@link apiBase}; absolute `http(s)://` URLs are
   * used as-is. An empty string disables end-session calls.
   */
  readonly endEndpoint?: string;

  /**
   * Endpoint for {@link AssistantApiClient.submitFeedback}. Relative values
   * are appended to {@link apiBase}; absolute `http(s)://` URLs are
   * used as-is.
   */
  readonly feedbackEndpoint?: string;
}

/**
 * Build a stateless {@link AssistantApiClient}. The returned
 * object holds no per-call state — there is nothing to reset and
 * no concurrency concern between methods, so the widget can
 * lazily reconstruct it whenever `apiBase` changes without
 * leaking lifecycle.
 */
export function createAssistantApiClient(
  opts: CreateAssistantApiClientOptions
): AssistantApiClient {
  const sessionEndpoint =
    opts.sessionEndpoint ?? DEFAULT_SESSION_ENDPOINT;
  const messageEndpoint = opts.messageEndpoint ?? DEFAULT_MESSAGE_ENDPOINT;
  const messageStreamEndpoint =
    opts.messageStreamEndpoint ?? DEFAULT_MESSAGE_STREAM_ENDPOINT;
  const historyEndpoint =
    opts.historyEndpoint ?? DEFAULT_HISTORY_ENDPOINT;
  const endEndpoint = opts.endEndpoint ?? DEFAULT_END_ENDPOINT;
  const feedbackEndpoint =
    opts.feedbackEndpoint ?? DEFAULT_FEEDBACK_ENDPOINT;
  const sessionUrl = resolveApiPath(opts.apiBase, sessionEndpoint);
  const messageUrl = resolveApiPath(opts.apiBase, messageEndpoint);
  const messageStreamUrl = resolveApiPath(opts.apiBase, messageStreamEndpoint);
  const feedbackUrl = resolveApiPath(opts.apiBase, feedbackEndpoint);
  const historyUrl = resolveApiPath(opts.apiBase, historyEndpoint);
  const endSessionUrl = resolveApiPath(opts.apiBase, endEndpoint);

  async function startSession(signal?: AbortSignal): Promise<AssistantSession> {
    const init: RequestInit = {
      method: 'POST',
      headers: { Accept: 'application/json' },
    };
    if (signal !== undefined) {
      init.signal = signal;
    }
    debugAssistantApiRequest('POST', sessionUrl);
    const response = await opts.fetch(sessionUrl, init);
    if (!response.ok) {
      throw new AssistantApiError(
        'session_failed',
        `Failed to start session (HTTP ${response.status})`,
        response.status
      );
    }
    const body = (await response.json()) as unknown;
    const session = parseAssistantSessionBody(body);
    if (session === null) {
      throw new AssistantApiError(
        'session_failed',
        'Session response did not match the documented shape'
      );
    }
    return session;
  }

  async function getChatHistory(
    conversationId: string,
    signal?: AbortSignal
  ): Promise<unknown> {
    if (historyEndpoint.trim().length === 0) {
      throw new AssistantApiError(
        'chat_history_disabled',
        'History endpoint is not configured'
      );
    }
    const init: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: conversationId }),
    };
    if (signal !== undefined) {
      init.signal = signal;
    }
    debugAssistantApiRequest('POST', historyUrl);
    const response = await opts.fetch(historyUrl, init);
    if (!response.ok) {
      const { errorCode, errorMessage } =
        await readHttpAssistantFailure(response);
      throw new AssistantApiError(
        errorCode,
        errorMessage,
        response.status
      );
    }
    try {
      return await response.json();
    } catch {
      throw new AssistantApiError(
        'protocol_error',
        'The assistant service returned an unreadable chat history response.',
        response.status
      );
    }
  }

  async function endSession(
    conversationId: string,
    signal?: AbortSignal
  ): Promise<void> {
    if (endEndpoint.trim().length === 0) {
      return;
    }
    const init: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: conversationId }),
    };
    if (signal !== undefined) {
      init.signal = signal;
    }
    /* The end-session endpoint is idempotent — ending an unknown
       or already-ended session still returns 2xx. We treat ANY 2xx
       as success and swallow the body. Non-2xx is rare — network
       or auth, mostly — and we surface it as an AssistantApiError
       so the caller can decide whether to log or ignore. */
    debugAssistantApiRequest('POST', endSessionUrl);
    const response = await opts.fetch(endSessionUrl, init);
    if (!response.ok) {
      throw new AssistantApiError(
        'end_session_failed',
        `Failed to end session (HTTP ${response.status})`,
        response.status
      );
    }
  }

  function sendMessageStream(
    request: AssistantRequest,
    signal?: AbortSignal
  ): AsyncIterable<AssistantStreamEvent> {
    return iterateMessageStream(opts.fetch, messageStreamUrl, request, signal);
  }

  async function sendMessage(
    request: AssistantRequest,
    signal?: AbortSignal
  ): Promise<AssistantResponse> {
    let response: Response;
    try {
      const init: RequestInit = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(request),
      };
      if (signal !== undefined) {
        init.signal = signal;
      }
      debugAssistantApiRequest('POST', messageUrl);
      response = await opts.fetch(messageUrl, init);
    } catch (err) {
      if (signal?.aborted === true) {
        throw err;
      }
      throw new AssistantApiError(
        'transport_error',
        assistantErrorMessageFromUnknown(err)
      );
    }

    if (!response.ok) {
      const { errorCode, errorMessage } =
        await readHttpAssistantFailure(response);
      throw new AssistantApiError(
        errorCode,
        errorMessage,
        response.status
      );
    }

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      throw new AssistantApiError(
        'protocol_error',
        'The assistant service returned an unreadable response.',
        response.status
      );
    }
    if (!isAssistantResponse(body)) {
      throw new AssistantApiError(
        'protocol_error',
        'The assistant service returned an unexpected response shape.',
        response.status
      );
    }
    return {
      ...body,
      actions: normalizeActions(body.actions),
    };
  }

  async function submitFeedback(
    payload: FeedbackPayload,
    signal?: AbortSignal
  ): Promise<FeedbackResponse> {
    /* The server endpoint does not exist yet — registration is
       P3-022b. We POST as if it does so the per-turn thumbs
       (P3-023a) and negative-reason card (P3-025a) UIs can wire
       against this method today; against the dev API every call
       returns 404, which lands here as
       FeedbackError { kind: 'server-error' }. That's the expected
       shape for this PR — see API-CONTRACT.md §5.5. */
    const init: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    };
    if (signal !== undefined) {
      init.signal = signal;
    }

    debugAssistantApiRequest('POST', feedbackUrl);
    const response = await opts.fetch(feedbackUrl, init);

    if (!response.ok) {
      throw await translateFeedbackHttpError(response);
    }

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      throw new FeedbackError(
        'server-error',
        'The feedback service returned an unreadable response.',
        response.status
      );
    }
    if (!isFeedbackResponse(body)) {
      throw new FeedbackError(
        'server-error',
        'The feedback service returned an unexpected response shape.',
        response.status
      );
    }
    return body;
  }

  return {
    startSession,
    sendMessage,
    sendMessageStream,
    endSession,
    getChatHistory,
    submitFeedback,
  };
}

/**
 * Resolve a widget-configured API path against {@link apiBase}.
 *
 * Relative paths (starting with `/` or not) are appended to
 * {@link apiBase} when it is set. Absolute `http(s)://` paths are
 * returned unchanged. When {@link path} already begins with
 * {@link apiBase}, it is returned as-is to avoid double-prefix joins
 * (e.g. api-base="/api/mwi" + path="/api/mwi/chatassistant/session").
 *
 * When {@link apiBase} is empty, {@link path} is normalised to a
 * root-relative path against the embedding page origin.
 *
 * Exported for unit tests.
 */
export function resolveApiPath(apiBase: string, path: string): string {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const base = apiBase.replace(/\/$/, '');
  if (base.length === 0) {
    return normalized;
  }
  if (normalized === base || normalized.startsWith(`${base}/`)) {
    return normalized;
  }
  return `${base}${normalized}`;
}

/**
 * Resolve the host-side token broker URL.
 *
 * When {@link apiBase} is a root-relative path prefix (e.g.
 * `/api/mwi`), {@link tokenEndpoint} is joined under that prefix —
 * the same BFF layout used for assistant endpoints. When
 * {@link apiBase} is an absolute origin (`https://…`) or empty,
 * {@link tokenEndpoint} stays page-relative (Sitecore CD broker on
 * the embedding host).
 */
export function resolveTokenEndpoint(
  apiBase: string,
  tokenEndpoint: string
): string {
  const trimmed = tokenEndpoint.trim();
  if (trimmed.length === 0) {
    return '';
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  if (apiBase.startsWith('/')) {
    return resolveApiPath(apiBase, trimmed);
  }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

/* ============================================================== *
 *  Streaming pipeline                                             *
 * ============================================================== */

/**
 * Drive the `POST /message/stream` request to completion as an
 * async iterable of typed events. Factored out of the closure so
 * the generator's `try/finally` is straightforward to read.
 *
 * Pre-stream errors (network throws, non-2xx responses, missing
 * body, decoder JSON failures) all funnel through
 * {@link translateHttpError} or yield a generic transport error
 * event — the caller never sees both an exception and an event
 * for the same request.
 */
async function* iterateMessageStream(
  fetchFn: AuthedFetch,
  url: string,
  request: AssistantRequest,
  signal: AbortSignal | undefined
): AsyncIterable<AssistantStreamEvent> {
  let response: Response;
  try {
    const init: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify(request),
    };
    if (signal !== undefined) {
      init.signal = signal;
    }
    debugAssistantApiRequest('POST', url);
    response = await fetchFn(url, init);
  } catch (err) {
    /* Caller-initiated abort: end iteration silently. The
       AbortError shape varies between runtimes (DOMException with
       `name: 'AbortError'`, plain Error, etc.); the
       authoritative signal is the AbortSignal itself. */
    if (signal?.aborted === true) {
      return;
    }
    yield {
      type: 'error',
      errorCode: 'transport_error',
      errorMessage: assistantErrorMessageFromUnknown(err),
    };
    return;
  }

  if (!response.ok) {
    yield await translateHttpError(response);
    return;
  }

  if (response.body === null) {
    /* The server committed a 200 but didn't ship a body. Treat as
       a protocol failure rather than an empty success. */
    yield {
      type: 'error',
      errorCode: 'protocol_error',
      errorMessage: 'The assistant service returned an empty response.',
    };
    return;
  }

  try {
    for await (const frame of parseSseStream(response.body)) {
      const mapped = mapSseFrame(frame);
      if (mapped !== null) {
        yield mapped;
        if (mapped.type === 'complete' || mapped.type === 'error') {
          /* Terminal frames end the stream. The server is supposed
             to close the body after sending these; we return early
             to release the reader and avoid waiting on a
             never-arriving extra frame. */
          return;
        }
      }
    }
    /* Stream ended without a terminal frame. The server is
       supposed to always send `complete` or `error`; missing both
       is a contract violation we surface for visibility. */
    yield {
      type: 'error',
      errorCode: 'protocol_error',
      errorMessage:
        'The assistant service closed the stream without a final response.',
    };
  } catch (err) {
    if (signal?.aborted === true) {
      return;
    }
    yield {
      type: 'error',
      errorCode: 'transport_error',
      errorMessage: 'The connection to the assistant service was lost.',
    };
  }
}

/**
 * Map one parsed SSE frame from the wire format into the typed
 * widget event. Returns `null` for unknown event names (defensive:
 * a future server release could add new event types and we
 * shouldn't crash; the widget treats them as "no-op" frames).
 *
 * Malformed JSON in the `data` payload is treated as a protocol
 * error rather than silently dropped — getting bad JSON over a
 * server-controlled wire format means the contract has drifted
 * and the user should be told something went wrong.
 */
function mapSseFrame(frame: SseEvent): AssistantStreamEvent | null {
  let payload: unknown;
  try {
    payload = JSON.parse(frame.data);
  } catch {
    return {
      type: 'error',
      errorCode: 'protocol_error',
      errorMessage: 'The assistant service returned an unreadable response.',
    };
  }

  switch (frame.event) {
    case 'delta': {
      if (
        payload !== null &&
        typeof payload === 'object' &&
        'textDelta' in payload &&
        typeof (payload as { textDelta: unknown }).textDelta === 'string'
      ) {
        return {
          type: 'delta',
          textDelta: (payload as { textDelta: string }).textDelta,
        };
      }
      return null;
    }
    case 'complete': {
      if (
        payload !== null &&
        typeof payload === 'object' &&
        'finalResponse' in payload &&
        isAssistantResponse(
          (payload as { finalResponse: unknown }).finalResponse
        )
      ) {
        const final = (payload as { finalResponse: AssistantResponse })
          .finalResponse;
        /* Same forward-compat narrowing as `startSession` —
           isAssistantResponse only checked that `actions` is an
           array; `normalizeActions` validates each element and
           drops unknown shapes. */
        return {
          type: 'complete',
          finalResponse: {
            ...final,
            actions: normalizeActions(final.actions),
          },
        };
      }
      return null;
    }
    case 'error': {
      if (payload !== null && typeof payload === 'object') {
        const obj = payload as { errorCode?: unknown; errorMessage?: unknown };
        const code =
          typeof obj.errorCode === 'string' ? obj.errorCode : 'agent_error';
        const message =
          typeof obj.errorMessage === 'string'
            ? obj.errorMessage
            : 'The assistant could not process that request. Please try again.';
        return { type: 'error', errorCode: code, errorMessage: message };
      }
      return null;
    }
    default:
      /* Unknown event name — no-op. */
      return null;
  }
}

/**
 * Translate a non-2xx response into a synthetic `'error'` stream
 * event. Tries to parse the body as JSON to lift the server's own
 * `error` / `message` fields (matches the shape the .NET endpoint
 * writes for `session_not_found`, `agent_timeout`, etc.); falls
 * back to status-driven defaults if the body is missing or
 * malformed.
 *
 * The status-code defaults intentionally collapse 5xx into a
 * single user-friendly message — the user doesn't care whether
 * the upstream model timed out (`504`) or rate-limited (`503`),
 * they care that they need to try again. The original status code
 * is preserved as `errorCode` only if the server didn't supply
 * its own.
 */
async function readHttpAssistantFailure(
  response: Response
): Promise<{ errorCode: string; errorMessage: string }> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    /* Not JSON, or body was already consumed — fall through. */
  }

  let code = defaultErrorCodeForStatus(response.status);
  let message = defaultErrorMessageForStatus(response.status);

  if (body !== null && typeof body === 'object') {
    const obj = body as Record<string, unknown>;
    if (typeof obj.error === 'string') {
      code = obj.error;
    }
    if (typeof obj.message === 'string') {
      message = obj.message;
    }
  }

  return { errorCode: code, errorMessage: message };
}

async function translateHttpError(
  response: Response
): Promise<AssistantStreamEvent> {
  const { errorCode, errorMessage } = await readHttpAssistantFailure(response);
  return { type: 'error', errorCode, errorMessage };
}

function defaultErrorCodeForStatus(status: number): string {
  if (status === 400) return 'invalid_request';
  if (status === 401) return 'unauthorized';
  if (status === 404) return 'session_not_found';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server_error';
  return 'request_failed';
}

function defaultErrorMessageForStatus(status: number): string {
  if (status === 401) {
    return 'Your session has expired. Please refresh the page and try again.';
  }
  if (status === 404) {
    return 'Your conversation has expired. Start a new one to continue.';
  }
  if (status === 429) {
    return "You're sending messages too quickly. Please wait a moment and try again.";
  }
  if (status >= 500) {
    return 'The assistant service is temporarily unavailable. Please try again.';
  }
  return 'The assistant could not process that request. Please try again.';
}

/* ============================================================== *
 *  Type guards + error type                                       *
 * ============================================================== */

/**
 * Normalises `POST /api/assistant/session` JSON into
 * {@link AssistantSession}.
 *
 * **Full contract** (API-CONTRACT.md §5.1): `conversationId`,
 * `createdAt`, `welcomeMessage`, `initialActions`.
 *
 * **Minimal host/BFF shape:** a non-empty `conversationId` string
 * alone is accepted; missing fields get safe defaults so
 * `/message/stream` can proceed. Unknown `initialActions` entries
 * are dropped via {@link normalizeActions}.
 */
function parseAssistantSessionBody(body: unknown): AssistantSession | null {
  if (body === null || typeof body !== 'object') {
    return null;
  }
  const obj = body as Record<string, unknown>;
  const rawId = obj.conversationId;
  const conversationId =
    typeof rawId === 'string' ? rawId.trim() : '';
  if (conversationId.length === 0) {
    return null;
  }
  const createdAt =
    typeof obj.createdAt === 'string' && obj.createdAt.length > 0
      ? obj.createdAt
      : new Date().toISOString();
  const welcomeMessage =
    typeof obj.welcomeMessage === 'string' ? obj.welcomeMessage : '';
  const rawActions = obj.initialActions;
  const initialActions = Array.isArray(rawActions)
    ? normalizeActions(rawActions)
    : [];
  return {
    conversationId,
    createdAt,
    welcomeMessage,
    initialActions,
  };
}

function isAssistantResponse(value: unknown): value is AssistantResponse {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.conversationId === 'string' &&
    typeof obj.message === 'string' &&
    Array.isArray(obj.actions)
  );
}

/**
 * Filter + narrow an `unknown[]` (`initialActions` from
 * `/session`, or `complete.finalResponse.actions` from
 * `/message/stream`) to a typed `AssistantAction[]`.
 *
 * Drop policy: any element that doesn't match a known
 * `AssistantAction` shape (wrong `type`, missing required field
 * for that variant, malformed `parameters`) is **silently
 * dropped** — never thrown — so a future server-emitted action
 * kind can't crash the widget. In dev builds we emit a
 * `console.warn` for visibility.
 *
 * The returned tuple deliberately excludes unknown shapes so the
 * widget's render path can be exhaustively typed against the
 * three known variants. P1-058c is where the
 * `'follow-up-prompt'` render branch lights up; today the widget
 * still receives the typed payload but pattern-matches it as a
 * no-op + warn.
 */
export function normalizeActions(
  actions: readonly unknown[]
): readonly AssistantAction[] {
  const out: AssistantAction[] = [];
  for (const candidate of actions) {
    const validated = validateAction(candidate);
    if (validated !== null) {
      out.push(validated);
      continue;
    }
    /* import.meta.env.DEV is provided by Vite's `vite/client`
       types; in unit tests under Vitest it's also `true`, which
       is what we want — test runs surface the warn just like a
       dev build does. Production builds replace this with the
       constant `false`, so the entire branch dead-code-eliminates. */
    if (import.meta.env.DEV) {
      /* eslint-disable-next-line no-console */
      console.warn(
        '[mwi-assistant] dropped unrecognized action payload:',
        candidate
      );
    }
  }
  return out;
}

function validateAction(value: unknown): AssistantAction | null {
  if (value === null || typeof value !== 'object') {
    return null;
  }
  const obj = value as Record<string, unknown>;
  if (typeof obj.label !== 'string' || obj.label.length === 0) {
    return null;
  }
  if (typeof obj.type !== 'string') {
    return null;
  }
  if (
    !KNOWN_ACTION_TYPES.has(
      obj.type as AssistantAction['type']
    )
  ) {
    return null;
  }
  switch (obj.type) {
    case 'intent': {
      const params = readOptionalStringRecord(obj.parameters);
      if (params === 'invalid') return null;
      const action: IntentAction = { type: 'intent', label: obj.label };
      return params === undefined
        ? action
        : { ...action, parameters: params };
    }
    case 'link-out': {
      if (typeof obj.url !== 'string' || obj.url.length === 0) {
        return null;
      }
      return { type: 'link-out', label: obj.label, url: obj.url };
    }
    case 'follow-up-prompt': {
      const params = readOptionalStringRecord(obj.parameters);
      if (params === 'invalid') return null;
      const action: FollowUpPromptAction = {
        type: 'follow-up-prompt',
        label: obj.label,
      };
      return params === undefined
        ? action
        : { ...action, parameters: params };
    }
    default:
      /* `KNOWN_ACTION_TYPES.has(...)` already filtered out
         everything outside the union, but the switch's default
         keeps the type system happy and acts as a defence in
         depth if the set drifts away from the union later. */
      return null;
  }
}

/**
 * Validate an optional `parameters` field on an action. Returns
 * `undefined` for "missing/null is fine", a frozen
 * `Record<string, string>` for a valid object, and the sentinel
 * `'invalid'` if the field exists but isn't a string→string map
 * (which fails the whole action — we don't want to silently drop
 * server-supplied context just because one value isn't a string).
 */
function readOptionalStringRecord(
  value: unknown
): Readonly<Record<string, string>> | undefined | 'invalid' {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value !== 'object') {
    return 'invalid';
  }
  const obj = value as Record<string, unknown>;
  for (const v of Object.values(obj)) {
    if (typeof v !== 'string') {
      return 'invalid';
    }
  }
  return obj as Readonly<Record<string, string>>;
}

function assistantErrorMessageFromUnknown(_err: unknown): string {
  /* Per AUTHENTICATION.md §13 we deliberately do not surface raw
     error message text from `fetch` rejections — the cause object
     can carry diagnostic info that the user-facing message
     shouldn't expose. The token-broker auth-client follows the
     same policy. */
  return 'Could not reach the assistant service. Please try again.';
}

/**
 * Thrown by `startSession`, `endSession`, and {@link AssistantApiClient.sendMessage}
 * on unexpected failures. `sendMessageStream` does NOT throw — it surfaces the same
 * conditions as `'error'` stream events instead, so the caller has
 * a single terminal-state surface.
 */
export class AssistantApiError extends Error {
  readonly errorCode: string;
  readonly status?: number;

  constructor(errorCode: string, message: string, status?: number) {
    super(message);
    this.name = 'AssistantApiError';
    this.errorCode = errorCode;
    if (status !== undefined) {
      this.status = status;
    }
  }
}

/**
 * Thrown by {@link AssistantApiClient.submitFeedback} on any
 * non-2xx HTTP response or unreadable body (P3-022a).
 *
 * `kind` is the stable wire-error contract documented in
 * API-CONTRACT.md §5.5; UI code may pattern-match on `kind` to
 * choose between "show inline retry" (`'server-error'` /
 * `'rate-limited'`), "silently drop" (`'unauthorized'` — the
 * widget will renew auth and retry through the existing P1-057
 * path), and "log + drop" (`'invalid-request'` — a contract bug,
 * not a user-facing one).
 *
 * 404 (endpoint not registered) intentionally collapses into
 * `'server-error'` rather than a fourth dedicated kind; today
 * P3-022b hasn't landed and every call 404s, but we don't want UI
 * code to special-case that transient state. Once the endpoint
 * lands, 404 disappears from this surface entirely.
 */
export class FeedbackError extends Error {
  readonly kind: FeedbackErrorKind;
  readonly status?: number;

  constructor(kind: FeedbackErrorKind, message: string, status?: number) {
    super(message);
    this.name = 'FeedbackError';
    this.kind = kind;
    if (status !== undefined) {
      this.status = status;
    }
  }
}

/**
 * Map a non-2xx feedback response onto a {@link FeedbackError}.
 *
 * Status mapping mirrors the streaming client's `defaultErrorCode-
 * ForStatus` table (P1-058) so the widget surfaces the same
 * terminal-state taxonomy on both the message and feedback flows:
 *
 *  - 400      → `'invalid-request'`
 *  - 401      → `'unauthorized'`
 *  - 429      → `'rate-limited'`
 *  - 5xx      → `'server-error'`
 *  - default  → `'server-error'` (covers 404 today — see
 *               FeedbackError remarks for why we don't break it
 *               out)
 */
async function translateFeedbackHttpError(
  response: Response
): Promise<FeedbackError> {
  const kind = feedbackErrorKindForStatus(response.status);
  let message = defaultFeedbackMessageForKind(kind);

  /* Best-effort lift of the server-supplied `message` field —
     same shape the assistant endpoints write
     (`{ error, message }`) so the wire format stays uniform. */
  try {
    const body = (await response.json()) as unknown;
    if (
      body !== null &&
      typeof body === 'object' &&
      typeof (body as { message?: unknown }).message === 'string'
    ) {
      message = (body as { message: string }).message;
    }
  } catch {
    /* Body wasn't JSON, was empty, or was already consumed —
       fall through to the status-driven default. */
  }

  return new FeedbackError(kind, message, response.status);
}

function feedbackErrorKindForStatus(status: number): FeedbackErrorKind {
  if (status === 400) return 'invalid-request';
  if (status === 401) return 'unauthorized';
  if (status === 429) return 'rate-limited';
  return 'server-error';
}

function defaultFeedbackMessageForKind(kind: FeedbackErrorKind): string {
  switch (kind) {
    case 'invalid-request':
      return 'The feedback request was rejected as invalid.';
    case 'unauthorized':
      return 'Your session has expired. Please refresh the page and try again.';
    case 'rate-limited':
      return "You're submitting feedback too quickly. Please wait a moment and try again.";
    case 'server-error':
      return 'The feedback service is temporarily unavailable. Please try again.';
  }
}

function isFeedbackResponse(value: unknown): value is FeedbackResponse {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.accepted === 'boolean' &&
    typeof obj.feedbackId === 'string' &&
    obj.feedbackId.length > 0
  );
}
